/**
 * uploadManager.ts
 *
 * Singleton background upload manager. Decouples media capture from upload —
 * blobs are saved to IndexedDB instantly, and uploads happen in the background
 * while the user browses freely.
 *
 * Job lifecycle: QUEUED → COMPRESSING → UPLOADING → FINALIZING → DONE / FAILED
 */

import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  type UploadTask,
} from 'firebase/storage';
import { doc, collection } from 'firebase/firestore';
import { storage, db } from './firebaseConfig';
import { getCurrentUser } from './authService';
import { MediaType } from '../types/task.types';
import { compressVideo } from './videoCompressionService';
import {
  getPendingUploads,
  getMediaBlob,
  removePendingUpload,
  clearStalePendingUploads,
} from './offlineSyncService';
import { saveDraft, deleteDraft } from './draftStorageService';
import { canTranscode, transcodeMedia } from './mediaTranscodeService';
import {
  createSubmissionDoc,
  makeFileName,
  getExtFromMime,
  triggerTranscription,
} from './participantSubmissionService';
import type { PhotoAnnotation } from '../types/photo.types';
import { collectPinAudioBlobs, substitutePinAudioUrls, type PinAudioResult } from './voicePinSubmit';
import { Sentry } from './sentryService';

// ─── Types ─────────────────────────────────────────────────────

export type UploadJobStatus =
  | 'queued'
  | 'compressing'
  | 'uploading'
  | 'finalizing'
  | 'done'
  | 'failed';

export interface UploadJob {
  id: number;
  submissionId: string;
  projectId: string;
  participantId: string;
  taskId: string;
  taskTitle: string;
  mediaType: string;
  notes: string;
  status: UploadJobStatus;
  progress: number;
  error: string | null;
  retryCount: number;
  createdAt: number;
  parentSubmissionId?: string | null;
  submissionType?: 'original' | 'redo' | 'addendum';
  attemptNumber?: number;
  /** Small base64 data URL for instant display while uploading */
  thumbnailUrl?: string | null;
  /** Photo annotation data — stored directly in Firestore, not as files */
  photoAnnotations?: PhotoAnnotation[];
}

type Listener = (jobs: UploadJob[]) => void;
type CancelListener = (taskId: string) => void;

// ─── Retry constants ──────────────────────────────────────────

const MAX_RETRIES = 3;
const RETRY_DELAYS = [5_000, 15_000, 45_000];

/**
 * Firestore write promises never settle while offline and ignore AbortSignal,
 * so an un-timed-out finalize deadlocks the whole single-flight queue.
 */
const FINALIZE_TIMEOUT_MS = 20_000;

/**
 * mediaTypes recover() can faithfully replay through processJob. Anything else
 * (today: 'signification', whose responses are JSON, not files) would be
 * silently rewritten as a video submission by mediaTypeMap's fallback.
 */
const REPLAYABLE_MEDIA_TYPES = new Set([
  'audio', 'photo', 'video', 'text', 'screen', 'guided',
]);

// ─── Upload Manager ───────────────────────────────────────────

class UploadManager {
  private jobs = new Map<number, UploadJob>();
  /** Blobs for jobs whose IndexedDB persistence failed — see enqueueInMemory. */
  private memoryBlobs = new Map<number, Blob[]>();
  private nextMemoryId = -1;
  private listeners = new Set<Listener>();
  private cancelListeners = new Set<CancelListener>();
  private processing = false;
  private currentAbort: AbortController | null = null;
  private currentJobId: number | null = null;
  private cancelledIds = new Set<number>();
  private retryTimers = new Map<number, ReturnType<typeof setTimeout>>();
  private _online = navigator.onLine;

  constructor() {
    window.addEventListener('online', () => {
      this._online = true;
      this.notify();
      this.processNext();
    });
    window.addEventListener('offline', () => {
      this._online = false;
      // Abort current upload — it'll re-queue on the catch path
      this.currentAbort?.abort();
      this.notify();
    });
  }

  get isOnline(): boolean {
    return this._online;
  }

  /** Subscribe to job state changes. Returns unsubscribe function. */
  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Subscribe to cancellations. Fires with the taskId AFTER the cancelled
   * upload has been demoted back to a resumable draft, so handlers can
   * safely mark the task as draft/resume in the UI.
   */
  onCancelled(listener: CancelListener): () => void {
    this.cancelListeners.add(listener);
    return () => this.cancelListeners.delete(listener);
  }

  /** Get current snapshot of all jobs. */
  getJobs(): UploadJob[] {
    return Array.from(this.jobs.values());
  }

  /** Enqueue a new upload job. */
  /**
   * Enqueue a capture whose blobs could NOT be persisted to IndexedDB.
   *
   * iOS in-app browsers (WhatsApp, Instagram — WKWebView) accept small
   * IndexedDB records but refuse to store Blobs (quota, or WebKit's
   * Blob-in-IDB bug). Persistence is a durability convenience, not a
   * prerequisite for uploading, so hold the blobs in memory and upload from
   * there rather than discarding a capture the participant already made.
   *
   * Trade-off, and why the caller warns the user: memory does not survive a
   * reload, so these jobs are NOT recoverable by the orphan scan.
   */
  enqueueInMemory(
    params: Omit<UploadJob, 'status' | 'progress' | 'error' | 'retryCount' | 'id'>,
    blobs: Blob[],
  ): void {
    // Negative ids cannot collide with IndexedDB's autoincrement keys.
    const id = this.nextMemoryId--;
    this.memoryBlobs.set(id, blobs);
    this.enqueue({ ...params, id });
  }

  /**
   * Blobs normally live in IndexedDB, but a job enqueued via enqueueInMemory
   * only exists in memory — check there first.
   */
  private async loadBlobs(job: UploadJob): Promise<Blob[] | null> {
    const inMemory = this.memoryBlobs.get(job.id);
    if (inMemory) return inMemory;

    const pending = (await getPendingUploads()).find(p => p.id === job.id);
    if (!pending) return null;

    const blobs: Blob[] = [];
    for (const key of pending.blobKeys) {
      const blob = await getMediaBlob(key);
      if (!blob) return null;
      blobs.push(blob);
    }
    return blobs;
  }

  enqueue(params: Omit<UploadJob, 'status' | 'progress' | 'error' | 'retryCount'>): void {
    const job: UploadJob = {
      ...params,
      status: 'queued',
      progress: 0,
      error: null,
      retryCount: 0,
    };
    this.jobs.set(job.id, job);
    this.notify();
    this.processNext();
  }

  /**
   * Kick the queue. processNext() bails when there is no signed-in user, and
   * recover() runs at boot BEFORE Firebase has restored the session — so a
   * job recovered from IndexedDB sat at 'queued' forever unless a network
   * event happened to fire. The provider calls this once auth resolves.
   */
  resume(): void {
    this.processNext();
  }

  /** Retry a failed job. */
  retry(jobId: number): void {
    const job = this.jobs.get(jobId);
    if (!job) return;
    job.status = 'queued';
    job.progress = 0;
    job.error = null;
    job.retryCount = 0;
    this.notify();
    this.processNext();
  }

  /**
   * Cancel a job. The captured media is demoted back to a resumable draft
   * (same shape a manual "save for later" produces) instead of being lost,
   * and the pending-upload record is removed so recover() can't resurrect
   * and submit a cancelled upload on the next app load.
   */
  cancel(jobId: number): void {
    const job = this.jobs.get(jobId);
    if (!job) return;
    const timer = this.retryTimers.get(jobId);
    if (timer) clearTimeout(timer);
    this.retryTimers.delete(jobId);
    this.jobs.delete(jobId);
    this.notify();
    if (job.status === 'done') return; // already submitted — nothing to undo

    // Abort only if this job is the one actually processing — an
    // unconditional abort would kill an unrelated in-flight upload. The
    // cancelled flag tells processJob's AbortError handler this abort was
    // a user cancel (owned here), not an offline pause (re-queue).
    if (this.currentJobId === jobId) {
      this.cancelledIds.add(jobId);
      this.currentAbort?.abort();
    }
    void this.demoteToDraft(job);
  }

  /**
   * Move a cancelled upload's media from the pending-upload store back into
   * the draft store, then notify cancel listeners so the UI can mark the
   * task resumable. Best-effort: if the record is already gone (upload
   * finalized in the race window), there is nothing to restore.
   */
  private async demoteToDraft(job: UploadJob): Promise<void> {
    try {
      const pending = (await getPendingUploads()).find(p => p.id === job.id);
      if (!pending) return;

      const blobs: Blob[] = [];
      for (const key of pending.blobKeys) {
        const blob = await getMediaBlob(key);
        if (blob) blobs.push(blob);
      }

      const lineage = pending.parentSubmissionId
        && (pending.submissionType === 'redo' || pending.submissionType === 'addendum')
        ? {
            parentSubmissionId: pending.parentSubmissionId,
            submissionType: pending.submissionType,
            attemptNumber: pending.attemptNumber || 2,
          }
        : undefined;

      // Text tasks carry their content in `notes` at submit time, but the
      // draft store keeps it in `textContent` (mirrors makeDraftHandler).
      const isText = job.mediaType === 'text';
      await saveDraft(job.taskId, {
        captureType: job.mediaType,
        notes: isText ? '' : job.notes,
        textContent: isText ? job.notes : undefined,
        blobs,
        ...(lineage ? { lineage } : {}),
      });
      await removePendingUpload(job.id).catch(() => {});
      this.cancelListeners.forEach(fn => fn(job.taskId));
    } catch {
      // Demotion is best-effort — worst case the pending record survives
      // and recover() re-offers the upload next session.
    }
  }

  /** Dismiss a completed or failed job from the UI. */
  dismiss(jobId: number): void {
    this.jobs.delete(jobId);
    this.notify();
  }

  /** Scan IndexedDB for orphaned pending uploads and re-enqueue them. */
  async recover(): Promise<void> {
    try {
      const pending = await getPendingUploads();
      // Records the age-purge below must not touch: everything we re-enqueue,
      // plus everything we deliberately skip (skipped ≠ orphaned — their data
      // is intact and still the participant's only copy).
      const keep = new Set<number>();
      for (const pu of pending) {
        if (!pu.id || this.jobs.has(pu.id)) continue;
        if (!REPLAYABLE_MEDIA_TYPES.has(pu.mediaType)) {
          keep.add(pu.id);
          // Replaying this through processJob would fabricate a submission of
          // the wrong type rather than restore the real one. Leave the record
          // alone (its data is still on disk) instead of corrupting it.
          console.warn(
            `[uploadManager] skipping recovery of pending upload ${pu.id}: `
            + `mediaType '${pu.mediaType}' has no faithful replay path`,
          );
          continue;
        }
        Sentry.addBreadcrumb({
          category: 'upload',
          message: 'recovered pending upload',
          level: 'info',
          data: { id: pu.id, mediaType: pu.mediaType, ageMs: Date.now() - pu.createdAt },
        });
        this.enqueue({
          id: pu.id,
          submissionId: pu.submissionId,
          projectId: pu.projectId,
          participantId: pu.participantId,
          taskId: pu.taskId,
          taskTitle: (pu as any).taskTitle || 'Upload',
          mediaType: pu.mediaType,
          notes: pu.notes,
          createdAt: pu.createdAt,
          parentSubmissionId: pu.parentSubmissionId,
          submissionType: pu.submissionType,
          attemptNumber: pu.attemptNumber,
          photoAnnotations: pu.photoAnnotations,
        });
      }

      // Purge genuinely orphaned records only AFTER re-enqueueing, exempting
      // everything now live in the queue. This used to run at module load in
      // participantSubmissionService, which meant it raced recover() and won —
      // deleting the participant's only copy of media older than 24h.
      for (const id of this.jobs.keys()) keep.add(id);
      await clearStalePendingUploads(undefined, keep).catch(() => {});
    } catch {
      // IndexedDB unavailable — nothing to recover
    }
  }

  // ─── Internal ─────────────────────────────────────────────────

  private notify(): void {
    const snapshot = this.getJobs();
    this.listeners.forEach(fn => fn(snapshot));
  }

  private updateJob(id: number, patch: Partial<UploadJob>): void {
    const job = this.jobs.get(id);
    if (!job) return;
    Object.assign(job, patch);
    this.notify();
  }

  private async processNext(): Promise<void> {
    if (this.processing) return;
    if (!this._online) return;

    // Check auth — null until Firebase restores the session; resume() re-kicks.
    const user = getCurrentUser();
    if (!user) return;

    // Only this account's jobs. participantId is `${uid}-${projectId}`, so a
    // record left behind by a previous sign-in on this device is skipped
    // rather than uploaded under the wrong user's Storage path.
    const next = Array.from(this.jobs.values())
      .find(j => j.status === 'queued' && j.participantId.startsWith(`${user.uid}-`));
    if (!next) return;

    this.processing = true;
    this.currentAbort = new AbortController();
    this.currentJobId = next.id;
    const { signal } = this.currentAbort;

    try {
      await this.processJob(next, user.uid, signal);
    } catch (err) {
      // Handled inside processJob
    } finally {
      this.processing = false;
      this.currentAbort = null;
      this.currentJobId = null;
      // Process next job in queue
      this.processNext();
    }
  }

  private async processJob(job: UploadJob, userId: string, signal: AbortSignal): Promise<void> {
    try {
      // ── 1. Load blobs (memory first, then IndexedDB) ──
      const blobs = await this.loadBlobs(job);
      if (!blobs) {
        this.updateJob(job.id, { status: 'failed', error: 'Upload data not found' });
        return;
      }

      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');

      // ── 2. Compress ──
      // Transcode-first (WebCodecs → WebM/Opus via mediaTranscodeService)
      // for video AND screen, with automatic fallbacks per the
      // media-compression spec: camera video falls back to the legacy
      // realtime compressor; screen falls back to uploading the original.
      // `compressed` — what actually RAN, not the media type — drives the
      // progress band, so non-compressed uploads report 0-90 honestly.
      const isAV = job.mediaType === 'video' || job.mediaType === 'screen';
      let processedBlobs = blobs;
      let compressed = false;

      if (isAV && (await canTranscode())) {
        this.updateJob(job.id, { status: 'compressing' });
        try {
          const out: Blob[] = [];
          const perBlob = 40 / blobs.length;
          for (let i = 0; i < blobs.length; i++) {
            if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
            const result = await transcodeMedia(
              blobs[i],
              job.mediaType === 'screen' ? 'screen' : 'video',
              (pct) => this.updateJob(job.id, { progress: Math.round(i * perBlob + (pct / 100) * perBlob) }),
              signal,
            );
            out.push(result);
          }
          processedBlobs = out;
          compressed = true;
        } catch (err) {
          if (err instanceof DOMException && err.name === 'AbortError') throw err;
          // Any transcode failure is non-fatal by contract — fall back.
          console.warn('[uploadManager] transcode failed — falling back:', err);
        }
      }

      if (!compressed && job.mediaType === 'video') {
        // Legacy realtime compressor — fallback for no-WebCodecs devices
        // and transcode failures. Unchanged from the pre-transcode app.
        this.updateJob(job.id, { status: 'compressing' });
        const legacyOut: Blob[] = [];
        const perBlob = 40 / blobs.length;
        for (let i = 0; i < blobs.length; i++) {
          if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
          const result = await compressVideo(blobs[i], (pct) => {
            const progress = (i * perBlob) + (pct / 100) * perBlob;
            this.updateJob(job.id, { progress: Math.round(progress) });
          }, signal);
          legacyOut.push(result.blob);
        }
        processedBlobs = legacyOut;
        compressed = true;
      }

      // ── 3. Upload to Firebase Storage ──
      this.updateJob(job.id, { status: 'uploading' });
      const uploadBase = compressed ? 40 : 0;
      const uploadRange = compressed ? 50 : 90;
      const urls: string[] = [];

      for (let i = 0; i < processedBlobs.length; i++) {
        if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
        const blob = processedBlobs[i];
        const ext = getExtFromMime(blob.type, job.mediaType === 'photo' ? 'jpg' : 'webm');
        const fileName = makeFileName(job.mediaType, i, ext);
        const perFile = uploadRange / processedBlobs.length;
        const fileBase = uploadBase + i * perFile;

        const url = await this.uploadBlob(
          blob, userId, job.submissionId, fileName,
          (pct) => {
            const progress = fileBase + (pct / 100) * perFile;
            this.updateJob(job.id, { progress: Math.round(progress) });
          },
          signal,
        );
        urls.push(url);
      }

      // ── 4. Create Firestore document ──
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
      this.updateJob(job.id, { status: 'finalizing', progress: 90 });

      const mediaTypeMap: Record<string, MediaType> = {
        audio: MediaType.Audio,
        photo: MediaType.Photo,
        video: MediaType.Video,
        text: MediaType.Text,
        screen: MediaType.Screen,
      };

      // Voice-pin audio: upload each pin's recording to Storage and
      // substitute audioUrl, stripping the raw Blob. Without this the
      // Blob reaches setDoc() → Firestore rejects "Unsupported field
      // value: a custom Blob object" → the whole job retry-loops and
      // fails. Mirrors participantSubmissionService.submitPhoto, which
      // the background-upload refactor failed to port.
      let finalAnnotations = job.photoAnnotations;
      if (job.photoAnnotations && job.photoAnnotations.length > 0) {
        const pinJobs = collectPinAudioBlobs(job.photoAnnotations);
        if (pinJobs.length > 0) {
          const pinResults: PinAudioResult[] = [];
          for (const pj of pinJobs) {
            if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
            const ext = getExtFromMime(pj.blob.type, 'webm');
            const fileName = `pin-audio_${pj.photoIndex}_${pj.pinId}_${Date.now()}.${ext}`;
            const url = await this.uploadBlob(
              pj.blob, userId, job.submissionId, fileName, () => {}, signal,
            );
            pinResults.push({ photoIndex: pj.photoIndex, pinId: pj.pinId, url });
          }
          finalAnnotations = substitutePinAudioUrls(job.photoAnnotations, pinResults);
        } else {
          // No blobs to upload, but strip any stray audioBlob defensively.
          finalAnnotations = substitutePinAudioUrls(job.photoAnnotations, []);
        }
      }

      // Pass job.submissionId as the doc id so this write is IDEMPOTENT: a
      // finalize that times out below and is retried overwrites the same doc
      // instead of creating a duplicate submission if the first write later
      // lands server-side. Storage paths already key off job.submissionId.
      const createdId = await Promise.race([
        createSubmissionDoc({
          submissionId: job.submissionId,
          projectId: job.projectId,
          participantId: job.participantId,
          taskId: job.taskId,
          mediaType: mediaTypeMap[job.mediaType] || MediaType.Video,
          mediaUrls: urls,
          notes: job.notes,
          parentSubmissionId: job.parentSubmissionId,
          submissionType: job.submissionType,
          attemptNumber: job.attemptNumber,
          photoAnnotations: finalAnnotations,
        }),
        new Promise<never>((_, reject) => setTimeout(
          () => reject(new Error('Finalize timed out — will retry')),
          FINALIZE_TIMEOUT_MS,
        )),
      ]);

      // transcribeSubmission is an HTTP callable, not a Firestore trigger —
      // it must be invoked from every submission path. The S48 auto-transcribe
      // was added only to the legacy direct-submit path, so audio submitted
      // through this manager was never transcribed. Use the RETURNED doc id:
      // createSubmissionDoc generates its own, distinct from job.submissionId.
      const hasVoicePins = !!finalAnnotations?.some(a => a.pins?.some(p => !!p.audioUrl));
      const transcribable = ['audio', 'video', 'screen'].includes(job.mediaType);
      if (!createdId.startsWith('preview-') && (transcribable || hasVoicePins)) {
        triggerTranscription(createdId, job.projectId).catch(err => {
          console.warn('[uploadManager] transcription trigger failed (non-fatal):', err);
        });
      }

      // ── 5. Cleanup ──
      // Only NOW is the draft redundant. It used to be deleted at submit time
      // (navigateAfterSubmit), which left the pending-upload record as the sole
      // copy of the media for the whole upload — and forever, if it never ran.
      // Delete-of-a-missing-draft is a no-op, so the common case (no draft, the
      // participant never tapped save-for-later) costs one harmless lookup.
      await removePendingUpload(job.id).catch(() => {});
      await deleteDraft(job.taskId).catch(() => {});
      // Free any in-memory blobs (no-op for IndexedDB-backed jobs). Only on
      // success — a failed job keeps them so retry can still upload.
      this.memoryBlobs.delete(job.id);
      this.updateJob(job.id, { status: 'done', progress: 100 });

    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        // User cancelled — cancel() already removed the job and owns the
        // draft demotion; nothing to do here.
        if (this.cancelledIds.has(job.id)) {
          this.cancelledIds.delete(job.id);
          return;
        }
        // Offline abort — re-queue without counting as retry
        this.updateJob(job.id, { status: 'queued', progress: 0 });
        return;
      }

      const msg = err instanceof Error ? err.message : String(err);
      const newRetry = job.retryCount + 1;

      if (newRetry <= MAX_RETRIES) {
        this.updateJob(job.id, {
          status: 'queued',
          progress: 0,
          error: msg,
          retryCount: newRetry,
        });
        // Schedule retry with backoff
        const delay = RETRY_DELAYS[Math.min(newRetry - 1, RETRY_DELAYS.length - 1)];
        const timer = setTimeout(() => {
          this.retryTimers.delete(job.id);
          this.processNext();
        }, delay);
        this.retryTimers.set(job.id, timer);
      } else {
        this.updateJob(job.id, { status: 'failed', error: msg });
        Sentry.captureMessage('upload failed after retries', {
          level: 'warning',
          extra: { mediaType: job.mediaType, error: msg, ageMs: Date.now() - job.createdAt },
        });
      }
    }
  }

  private uploadBlob(
    blob: Blob,
    userId: string,
    submissionId: string,
    fileName: string,
    onProgress: (pct: number) => void,
    signal: AbortSignal,
  ): Promise<string> {
    const storagePath = `users/${userId}/submissions/${submissionId}/${fileName}`;
    const storageRef = ref(storage, storagePath);
    const contentType = blob.type || 'application/octet-stream';
    const task: UploadTask = uploadBytesResumable(storageRef, blob, { contentType });

    const onAbort = () => task.cancel();
    signal.addEventListener('abort', onAbort, { once: true });

    return new Promise((resolve, reject) => {
      task.on(
        'state_changed',
        (snap) => onProgress((snap.bytesTransferred / snap.totalBytes) * 100),
        (error) => {
          signal.removeEventListener('abort', onAbort);
          if ((error as any)?.code === 'storage/canceled') {
            reject(new DOMException('Cancelled', 'AbortError'));
          } else {
            reject(error);
          }
        },
        async () => {
          signal.removeEventListener('abort', onAbort);
          resolve(await getDownloadURL(task.snapshot.ref));
        },
      );
    });
  }
}

// ─── Singleton (window key survives HMR) ─────────────────────

const MGR_KEY = '__DEKKO_UPLOAD_MGR__';

export function getUploadManager(): UploadManager {
  if (!(window as any)[MGR_KEY]) {
    (window as any)[MGR_KEY] = new UploadManager();
  }
  return (window as any)[MGR_KEY];
}
